import * as Models from "@/redux/models"
import { createSelector } from "@reduxjs/toolkit"
import { addDays, hoursToMilliseconds } from "date-fns"

import { isNumberValid } from "@/base/type-guards"
import { getLoadActiveUserFetchStatusFromState } from "./requests.reducer"
import type { RootState } from "./root.reducer"
import { getActiveFarmFromState, getUserIsAdminFromState } from "./selectors"

/**
 * Is this provided farm id associated with a demo farm account?
 */
export function isDemoFarmId(farmId: number | null | undefined) {
  return typeof farmId === "number" && farmId < 0
}

/**
 * Demo farms should have a flag marking them as demo farms
 * and also have a negative id.
 */
export const getAllRealFarmsFromState = createSelector(
  Models.userFarm.selectAll,
  (farms) => farms.filter((f) => f.isDemo === false && !isDemoFarmId(f.id)),
)
export const getRealFarmCountFromState = createSelector(
  getAllRealFarmsFromState,
  (farms) => farms.length,
)

export const getUserFarmStatusFromState = createSelector(
  [getLoadActiveUserFetchStatusFromState, getRealFarmCountFromState],
  (fetchStatus, farmCount) => {
    if (farmCount > 0) {
      return "HAS_FARM"
    }
    if (fetchStatus === "pending") {
      return "LOADING"
    }
    if (fetchStatus === "fulfilled" && farmCount === 0) {
      return "NO_FARM"
    }
    return "UNKNOWN"
  },
)
export const getIsMultifarmUserFromState = createSelector(
  getRealFarmCountFromState,
  (count) => count > 1,
)

/**
 * Start warning users this many days before the due date
 */
const WARNING_PERIOD_BEFORE_DUE_DATE_DAYS = 10

/**
 * This many days after the due date, the account is locked
 */
const GRACE_PERIOD_DAYS_AFTER_DUE_DATE = 10

export function getFarmAccountSubscriptionLockDate(dueDate: Date | number) {
  return addDays(dueDate, GRACE_PERIOD_DAYS_AFTER_DUE_DATE)
}

export const getActiveFarmSubscriptionDueDate = createSelector(
  getActiveFarmFromState,
  (farm) => {
    // return subDays(new Date(), 40)
    const subscriptionRenewalDueDate = farm?.subscriptionRenewalDueDate
    if (typeof subscriptionRenewalDueDate === "string") {
      return new Date(subscriptionRenewalDueDate).getTime()
    }
    return undefined
  },
)

export function getIsActiveFarmAccountLockedDueToNonPaymentFromState(
  state: RootState,
) {
  const isAdminUser = getUserIsAdminFromState(state)
  const farmSubscriptionDueDate = getActiveFarmSubscriptionDueDate(state)
  if (isNumberValid(farmSubscriptionDueDate)) {
    const farmSubscriptionDueDateTimestamp = new Date(farmSubscriptionDueDate)

    const lockDate = getFarmAccountSubscriptionLockDate(
      farmSubscriptionDueDateTimestamp,
    )

    const now = new Date().getTime()
    if (lockDate.getTime() < now && !isAdminUser) {
      // return "account-locked"
      return true
    }
  }
  return false
}

export function isDateWithinSubscriptionWarningThreshold(
  farmSubscriptionDueDate: number,
) {
  // If the subscription date is within X days of today, show the warning
  const msInFuture = farmSubscriptionDueDate - new Date().getTime()

  const hoursUntilDueDate = WARNING_PERIOD_BEFORE_DUE_DATE_DAYS * 24
  const msUntilDueDate = hoursToMilliseconds(hoursUntilDueDate)
  if (msInFuture < msUntilDueDate) {
    return true
  }

  return false
}
